import { Formik } from 'formik';
import React from 'react';
import {
  Container,
  Divider,
  Form,
  Header,
  Button,
  Message,
} from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { Provider } from 'types/provider';
import { request } from 'utils/api';
import DropdownField from 'components/form-fields/formik/DropdownField';
import CheckboxField from 'components/form-fields/formik/CheckboxField';
import TimeField from 'components/form-fields/formik/TimeField';
import styles from './Settings.module.css';

const SettingsChargeStations: React.FC<{
  provider: Partial<Provider>;
}> = ({ provider }) => {
  const { t } = useTranslation();
  return (
    <Container>
      <Header as="h2" style={{ padding: '0', margin: '0' }}>
        {t('editSandboxChargeStations.title', 'Charge Stations')}
      </Header>
      <Divider />
      <p>
        {t('editSandboxChargeStations.description', 'Charge station settings')}
      </p>
      <Formik
        enableReinitialize
        initialValues={{
          enableAlternativePaymentMethods:
            provider.enableAlternativePaymentMethods,
          enableChargeStationPairChargeCard:
            provider.enableChargeStationPairChargeCard,
          enableChargingStationUI: provider.enableChargingStationUI,
          enableSmartCharging: provider.enableSmartCharging,
          defaultCsmeClientIdsOnAllAccounts: (provider as any)
            .defaultCsmeClientIdsOnAllAccounts,
          enableCustomStationId: provider.enableCustomStationId,
          chargingStationInactivityCheckIntervalInSeconds:
            provider.chargingStationInactivityCheckIntervalInSeconds,
          evseIssueEmails: provider.evseIssueEmails,
        }}
        onSubmit={(values) => {
          request({
            method: 'PATCH',
            path: `/1/sandboxes/${(provider as any)._id}/settings`,
            body: {
              ...provider,
              ...values,
            },
          });
        }}>
        {({ handleSubmit, values, isSubmitting, isValid, dirty }) => {
          return (
            <Form>
              <Form.Field className={styles.productProposition}>
                <CheckboxField
                  toggle
                  label={t(
                    'editSandboxChargeStations.enableAlternativePaymentMethods',
                    'Enable Support for Payment Terminals (e.g. Alpitronic, Payter)'
                  )}
                  name="enableAlternativePaymentMethods"
                />
                <Message info>
                  {t(
                    'editSandboxChargeStations.enableAlternativePaymentMethodsExtra',
                    'This option is only supported for chargers of the following vendors: Alpitronic, Payter'
                  )}
                  {t(
                    'editSandboxChargeStations.enableAlternativePaymentMethodsInfo',
                    'Integrates with physical payment terminals at charging stations, allowing customers to pay directly at the point of charge.'
                  )}
                </Message>
              </Form.Field>
              <Form.Field className={styles.productProposition}>
                <CheckboxField
                  toggle
                  label={t(
                    'editSandboxChargeStations.enablePairChargeCard',
                    'Enable "Pair Charge Card"'
                  )}
                  name="enableChargeStationPairChargeCard"
                />
                <Message info>
                  <Message.Content>
                    {t(
                      'editSandboxChargeStations.enablePairChargeCardExtra',
                      'This option is only supported for chargers of the following vendors: DiniTech'
                    )}
                  </Message.Content>
                </Message>
              </Form.Field>
              <Form.Field className={styles.productProposition}>
                <CheckboxField
                  toggle
                  label={t(
                    'editSandboxChargeStations.enableChargingStationUI',
                    'Enable Charging Station UI.'
                  )}
                  name="enableChargingStationUI"
                />
                <Message info>
                  {t(
                    'editSandboxChargeStations.enableChargingStationUIInfo',
                    'Provides a user interface directly on charging stations for local interaction, status display, and payment processing.'
                  )}
                </Message>
              </Form.Field>
              <Form.Field className={styles.productProposition}>
                <CheckboxField
                  toggle
                  label={t(
                    'editSandboxChargeStations.enableSmartCharging',
                    'Enable Pairing an EVSE to a Smart Charging Provider'
                  )}
                  name="enableSmartCharging"
                />
                <Message info>
                  {t(
                    'editSandboxChargeStations.enableSmartChargingInfo',
                    'Enables integration with smart charging providers for optimized energy management, load balancing, and grid-responsive charging.'
                  )}
                </Message>
              </Form.Field>
              <Form.Field className={styles.productProposition}>
                <CheckboxField
                  toggle
                  label={t(
                    'editSandboxChargeStations.enableCustomStationId',
                    'Enable "Custom Station ID"'
                  )}
                  name="enableCustomStationId"
                />
                <Message info>
                  {t(
                    'editSandboxChargeStations.enableCustomStationIdInfo',
                    'Enables the use of custom station IDs for charging stations.'
                  )}
                </Message>
              </Form.Field>
              <Form.Field className={styles.productProposition}>
                <TimeField
                  label={t(
                    'editSandboxChargeStations.chargingStationInactivityCheckIntervalInSeconds',
                    'Charging Station Inactivity Check Interval'
                  )}
                  name="chargingStationInactivityCheckIntervalInSeconds"
                />
                <Message info>
                  <Message.Content>
                    {t(
                      'editSandboxChargeStations.chargingStationInactivityCheckIntervalInfo',
                      'Default value is 24 hours and minimum value is 10 minutes. If a charging station remains inactive for this period, an EVSE Issue will be created.'
                    )}
                  </Message.Content>
                </Message>
                <DropdownField
                  name="evseIssueEmails"
                  selection
                  allowAdditions
                  multiple
                  options={
                    values.evseIssueEmails?.map((value) => {
                      return {
                        value,
                        text: value,
                      };
                    }) || []
                  }
                  label="EVSE Issues E-mail Recipients"
                />
              </Form.Field>
              <Button
                disabled={isSubmitting || !isValid}
                onClick={() => handleSubmit()}
                primary>
                {t('save', 'Save')}
              </Button>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};

export default SettingsChargeStations;
